/**
 * Minimal, framework-free i18n runtime.
 *
 * Design goals:
 *   1. Built-in locales ship as standalone JSON files in `./locales/*.json`.
 *      Adding a new language is as simple as dropping a new JSON file next
 *      to the existing ones and rebuilding the shared package — no code
 *      changes required.
 *   2. New locales can also be registered at runtime via {@link loadLocale}.
 *      The server calls this once on boot (after reading any user-provided
 *      JSON packs from disk), and the admin console uses the same path to
 *      push edits/translations live without a rebuild.
 *   3. Translation *keys* are strings, translation *values* are plain
 *      strings with `{name}`-style placeholder interpolation. No inflection,
 *      no plural rules, no ICU — we keep it tiny on purpose.
 *
 * Fallback order for `t(key)`:
 *   1. the explicitly passed `locale` (if any) — or `currentLocale`
 *   2. the fallback locale (`en`)
 *   3. the raw key, so a missing translation is still visibly wrong and
 *      greppable rather than a silent empty string.
 */
// Node 22+ ESM requires an explicit `type: json` import attribute for
// JSON modules. Vite/Rollup and TypeScript 5.3+ pass the attribute
// through untouched, so this single syntax works for both the server
// (native Node ESM) and the client (bundled).
import enLocale from './locales/en.json' with { type: 'json' };
import esLocale from './locales/es.json' with { type: 'json' };
import ptLocale from './locales/pt.json' with { type: 'json' };
const FALLBACK_LOCALE = 'en';
const translations = {
    en: { ...enLocale },
    es: { ...esLocale },
    pt: { ...ptLocale },
};
const availableLocales = new Set(['en', 'es', 'pt']);
let currentLocale = FALLBACK_LOCALE;
/**
 * Pretty display name for each known locale (in its own language). Built-in
 * languages ship with a friendly name; runtime-loaded packs default to the
 * upper-cased code unless an operator registers a name via {@link registerLocaleName}.
 *
 * The auth screen, main menu, and pause menu's General pane all read from
 * here when rendering the language selector so we don't have to keep three
 * lookup tables in sync.
 */
const localeDisplayNames = {
    en: 'English',
    es: 'Español',
    pt: 'Português',
};
export function setLocale(locale) {
    currentLocale = locale;
}
export function getLocale() {
    return currentLocale;
}
export function t(key, localeOrParams, maybeParams) {
    const locale = typeof localeOrParams === 'string' ? localeOrParams : undefined;
    const params = typeof localeOrParams === 'string' ? maybeParams : localeOrParams;
    const l = locale ?? currentLocale;
    let value = translations[l]?.[key]
        ?? translations[FALLBACK_LOCALE]?.[key]
        ?? key;
    if (params) {
        for (const [k, v] of Object.entries(params)) {
            value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        }
    }
    return value;
}
/** List every locale with at least one registered entry. Order is insertion. */
export function getAvailableLocales() {
    return Array.from(availableLocales);
}
/**
 * Friendly display name for a locale (e.g. "English", "Español"). Falls
 * back to the upper-cased locale code for unknown codes so a hand-loaded
 * `fr.json` still surfaces something readable in the language picker
 * even if no display name was registered.
 */
export function getLocaleDisplayName(locale) {
    return localeDisplayNames[locale] ?? locale.toUpperCase();
}
/**
 * Register a friendly display name for a locale. Used by operators who
 * drop a new language pack into `data/locales` and want it to surface
 * in the in-game language picker with a real label instead of the
 * stringified locale code.
 */
export function registerLocaleName(locale, name) {
    if (!locale || !name)
        return;
    localeDisplayNames[locale] = name;
}
/**
 * Does the given key have a translation registered at all (in any locale)?
 * Useful for callers that want to fall back to a hand-authored label when no
 * i18n entry exists yet.
 */
export function hasTranslation(key) {
    for (const locale of availableLocales) {
        if (key in (translations[locale] ?? {}))
            return true;
    }
    return false;
}
/**
 * Register (or override) a single translation entry. Intended for content
 * bootstrapping — shared constants and db-loaded content can publish their
 * localized names/descriptions into the same i18n registry the UI uses.
 *
 * Any locale referenced here is automatically added to {@link getAvailableLocales}.
 */
export function registerTranslation(key, values) {
    for (const [loc, v] of Object.entries(values)) {
        if (v === undefined || v === null)
            continue;
        if (!translations[loc])
            translations[loc] = {};
        translations[loc][key] = v;
        availableLocales.add(loc);
    }
}
/**
 * Bulk-register many translations at once. The outer record is keyed by
 * locale, the inner by translation key. Missing locales are simply skipped.
 */
export function registerTranslations(entries) {
    for (const [loc, dict] of Object.entries(entries)) {
        if (!dict)
            continue;
        if (!translations[loc])
            translations[loc] = {};
        availableLocales.add(loc);
        for (const [k, v] of Object.entries(dict)) {
            translations[loc][k] = v;
        }
    }
}
/**
 * Replace (or introduce) an entire locale in one go. Existing keys for
 * the locale are preserved unless `replace: true` is passed; new keys are
 * merged in. Use this for loading whole language packs from JSON files at
 * runtime (e.g. an admin-uploaded `fr.json`).
 */
export function loadLocale(code, dict, opts = {}) {
    if (opts.replace || !translations[code]) {
        translations[code] = { ...dict };
    }
    else {
        translations[code] = { ...translations[code], ...dict };
    }
    availableLocales.add(code);
}
/** Snapshot of all registered translations — used by the admin tools. */
export function getAllTranslations() {
    const out = {};
    for (const locale of availableLocales) {
        out[locale] = { ...(translations[locale] ?? {}) };
    }
    return out;
}
export function itemNameKey(kind, id) {
    return `item.${kind}.${id}.name`;
}
export function itemDescriptionKey(kind, id) {
    return `item.${kind}.${id}.description`;
}
export function itemLabel(kind, id, fallback, locale) {
    const key = itemNameKey(kind, id);
    if (!hasTranslation(key))
        return fallback ?? id;
    return t(key, locale);
}
export function itemDescription(kind, id, fallback, locale) {
    const key = itemDescriptionKey(kind, id);
    if (!hasTranslation(key))
        return fallback ?? '';
    return t(key, locale);
}
export function raceNameKey(raceId) {
    return `race.${raceId}.name`;
}
export function raceDescriptionKey(raceId) {
    return `race.${raceId}.description`;
}
export function resourceNameKey(tier) {
    return `resource.${tier}.name`;
}
export function shipNameKey(shipClass) {
    return `ship.${shipClass}.name`;
}
export function rankNameKey(rank) {
    return `rank.${rank}.name`;
}
export function npcNameKey(key) {
    return `npc.${key}.name`;
}
export function npcDescriptionKey(key) {
    return `npc.${key}.description`;
}
//# sourceMappingURL=index.js.map